import React from 'react'
import {
  axisClasses,
  BarChart,
  ChartsTooltip,
  LegendRendererProps,
  LineChart,
  pieArcLabelClasses,
  PieChart,
  ScatterChart,
} from '@mui/x-charts'
import { DefaultizedPieValueType } from '@mui/x-charts/models/seriesType/pie'
import { formatCurrency } from 'Components/Charts/CampaignMultiLineChart/utils'
import {
  ChartSeriesData,
  Goal,
  GoalType,
  MetricType,
} from 'Generated/graphql-types'
import { resiliaPalette } from 'Components/Pages/FunderDashboard/FunderGoals/utils'
import { ChartAxisOptions } from './ChartAxisOptions'
import {
  Chart,
  ChartTypeOptionsEnum,
  DimensionsData,
  SeriesData,
} from '../types'
import { calculateAdditionalChartSeries, formatNumberCompact } from '../utils'

type GoalChartProps = {
  chart?: Chart
  chartType: ChartTypeOptionsEnum
  goal: Goal
  pieChartSeriesData: ChartSeriesData[]
  series: SeriesData[] | DimensionsData[]
  campaignTarget?: number
  height?: number
}

// Format options for date display
const dateFormatOptions: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
}

// Helper function to determine appropriate tick count based on data points
const getAppropriateTickCount = (dates: Date[]): number => {
  const count = dates.length
  if (count <= 6) return count
  if (count <= 12) return Math.ceil(count / 2)
  if (count <= 24) return Math.ceil(count / 4)
  return Math.ceil(count / 6)
}

export const GoalChart = ({
  chart,
  chartType,
  goal,
  pieChartSeriesData,
  series,
  campaignTarget,
  height,
}: GoalChartProps) => {
  const chartHeight = height || 500
  const dateStrings = series[0].data.map(({ x }) => x)

  // Parse the date strings to Date objects for proper time scale
  const dateObjects = dateStrings.map((dateStr) => new Date(dateStr))

  // Calculate appropriate number of ticks based on data length
  const tickCount = getAppropriateTickCount(dateObjects)

  const shouldAverage = goal.metric === MetricType.Average
  const calculatedSeries = calculateAdditionalChartSeries(series, shouldAverage)

  // Create series data in the format expected by MUI X-Charts
  const seriesData = (series as SeriesData[]).map((category) => ({
    data: category.data.map(({ y }) => y),
    label: category.title,
  }))

  const allSeries =
    seriesData.length === 1 ? seriesData : [calculatedSeries, ...seriesData]

  // AML: Seems like this should look for the target in the chart first, seemingly goal.target has been deprecated
  const target = campaignTarget || chart?.target || goal.target
  const isCampaignGoal = goal.type === GoalType.Campaign
  const yAxisLabel = isCampaignGoal
    ? 'Net Raised ($)'
    : chart?.unitOfMeasurement

  const legendSlotProps: Partial<LegendRendererProps> = {
    direction: 'row',
    itemMarkWidth: 15,
    itemMarkHeight: 5,
    position: { vertical: 'bottom', horizontal: 'middle' },
    padding: height && height <= 300 ? 4 : -8,
    itemGap: 20,
  }

  // Common style to hide the x-axis label across all chart types
  const commonChartSx = {
    [`& .${axisClasses.left} .${axisClasses.label}`]: {
      transform: 'translateX(-10px)',
    },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '& .MuiChartsAxis-bottom .MuiChartsAxis-label': {
      display: 'none',
    },
  }

  switch (chartType) {
    case ChartTypeOptionsEnum.pie: {
      const data = pieChartSeriesData.map((serie, index) => {
        const values = serie.data.map((dataPoint) => dataPoint?.y ?? 0)
        const value = values.reduce((acc, v) => acc + v, 0) || 0
        return {
          id: index,
          value,
          label: serie.title,
        }
      })

      const totalValue = data.reduce((acc, { value }) => acc + value, 0)

      const pieChartData = [
        {
          data,
          arcLabel: (item: DefaultizedPieValueType) =>
            `${((item.value / totalValue) * 100).toFixed(1)}%`,
        },
      ]

      return (
        <PieChart
          colors={resiliaPalette}
          height={chartHeight}
          margin={{
            top: height && height <= 300 ? 5 : 20,
            bottom: height && height <= 300 ? 70 : 80, // Less bottom margin for smaller charts
          }}
          series={pieChartData}
          slotProps={{
            legend: legendSlotProps,
          }}
          sx={{
            ...commonChartSx,
            [`& .${pieArcLabelClasses.root}`]: {
              fill: 'white',
              fontWeight: 'bold',
            },
          }}
        />
      )
    }
    case ChartTypeOptionsEnum.bar:
      return (
        <BarChart
          colors={resiliaPalette}
          margin={{
            top: height && height <= 300 ? 5 : 20,
            bottom: height && height <= 300 ? 70 : 80, // Less bottom margin for smaller charts
          }}
          series={allSeries.map((s) => ({
            ...s,
            valueFormatter: (value: number | null) =>
              // eslint-disable-next-line no-nested-ternary
              value === null
                ? 'No data'
                : isCampaignGoal
                ? formatCurrency(value)
                : formatNumberCompact(value),
          }))}
          height={chartHeight}
          slotProps={{
            legend: legendSlotProps,
          }}
          xAxis={[
            {
              id: 'Dates',
              data: dateStrings.map((dateStr) =>
                new Date(dateStr).toLocaleDateString(
                  undefined,
                  dateFormatOptions
                )
              ),
              scaleType: 'band',
              label: '', // Remove the "Date" label
              tickInterval: (_, index) =>
                index % Math.ceil(dateStrings.length / tickCount) === 0,
            },
          ]}
          sx={commonChartSx}
          yAxis={[
            {
              valueFormatter: formatNumberCompact,
              min: 0,
              max:
                Math.max(target ?? 0, ...allSeries.flatMap((s) => s.data)) *
                1.1,
            },
          ]}
        >
          <ChartAxisOptions
            target={target}
            unitOfMeasurement={yAxisLabel}
            isCampaignGoal={isCampaignGoal}
          />
        </BarChart>
      )
    case ChartTypeOptionsEnum.dot: {
      // Extract dates from the first series - all series will have same dates
      const dates = series[0].data.map((d) => new Date(d.x).getTime())

      // For scatter charts, only use the original series data (no total)
      // Create basic scatter series data without incompatible props
      const scatterSeries = seriesData.map((category, index) => ({
        type: 'scatter' as const,
        id: category.label,
        label: category.label,
        data: dates.map((timestamp, i) => ({
          id: `${category.label}-${i}`,
          x: timestamp,
          y: category.data[i] || 0,
        })),
        color: resiliaPalette[index % resiliaPalette.length],
        markerSize: 5,
        // Add valueFormatter to format the y-value in tooltips
        valueFormatter: (value: any) => {
          const numValue = typeof value === 'object' ? value.y : value
          if (numValue === null || numValue === undefined) return 'No data'
          return isCampaignGoal
            ? formatCurrency(numValue)
            : formatNumberCompact(numValue)
        },
      }))

      return (
        <ScatterChart
          colors={resiliaPalette}
          height={chartHeight}
          series={scatterSeries}
          slotProps={{
            legend: legendSlotProps,
          }}
          xAxis={[
            {
              scaleType: 'time',
              tickLabelStyle: {
                angle: height && height <= 300 ? 30 : 45,
                textAnchor: 'start' as const,
                fontSize: height && height <= 300 ? 10 : 12,
              },
              min: Math.min(...dates),
              max: Math.max(...dates),
              tickNumber: tickCount,
              label: '', // Remove the "Date" label
              // Enhanced formatter for displaying dates in tooltips and axis
              valueFormatter: (value) =>
                // This will format dates consistently in both axis labels and tooltips
                new Date(value).toLocaleDateString(
                  undefined,
                  dateFormatOptions
                ),
            },
          ]}
          yAxis={[
            {
              // Enhanced formatter for better tooltip display
              valueFormatter: (value) => {
                // This formatter affects both axis labels and tooltips
                const formatted = isCampaignGoal
                  ? formatCurrency(value)
                  : formatNumberCompact(value)
                return formatted
              },
              min: 0,
              max:
                Math.max(target ?? 0, ...seriesData.flatMap((s) => s.data)) *
                1.1,
            },
          ]}
          margin={{
            top: height && height <= 300 ? 5 : 20,
            bottom: height && height <= 300 ? 85 : 80, // More bottom margin for smaller charts to accommodate rotated labels
            left: height && height <= 300 ? 40 : 50,
            right: height && height <= 300 ? 10 : 20,
          }}
          sx={commonChartSx}
        >
          <ChartsTooltip
            trigger='item'
            // Format series names for better tooltip display
            itemLabelFormatter={(seriesVals: { label: string }) =>
              seriesVals.label
            }
            // Format x-values (dates) in tooltips
            axisLabelFormatter={(value: unknown, axis: { type: string }) => {
              if (axis.type === 'x') {
                return new Date(value as number).toLocaleDateString(
                  undefined,
                  dateFormatOptions
                )
              }
              return String(value)
            }}
          />
          <ChartAxisOptions
            target={target}
            unitOfMeasurement={yAxisLabel}
            isCampaignGoal={isCampaignGoal}
          />
        </ScatterChart>
      )
    }
    case ChartTypeOptionsEnum.line:
      return (
        <LineChart
          colors={resiliaPalette}
          height={chartHeight}
          margin={{
            top: height && height <= 300 ? 5 : 20,
            bottom: height && height <= 300 ? 70 : 80, // Less bottom margin for smaller charts
          }}
          xAxis={[
            {
              data: dateObjects,
              scaleType: 'time',
              tickLabelStyle: {
                angle: height && height <= 300 ? 30 : 45,
                textAnchor: 'start' as const,
                fontSize: height && height <= 300 ? 10 : 12,
              },
              // Limit the number of ticks displayed
              tickNumber: tickCount,
              label: '', // Remove the "Date" label
              // Format the date labels with month, day and year
              valueFormatter: (date) =>
                date.toLocaleDateString(undefined, dateFormatOptions),
            },
          ]}
          series={allSeries.map((s) => ({
            ...s,
            valueFormatter: (value: number | null) =>
              // eslint-disable-next-line no-nested-ternary
              value === null
                ? 'No data'
                : isCampaignGoal
                ? formatCurrency(value)
                : formatNumberCompact(value),
            connectNulls: true, // Connect across null values for smoother lines
          }))}
          slotProps={{
            legend: legendSlotProps,
          }}
          sx={commonChartSx}
          yAxis={[
            {
              valueFormatter: formatNumberCompact,
              min: 0,
              max:
                Math.max(target ?? 0, ...allSeries.flatMap((s) => s.data)) *
                1.1,
            },
          ]}
        >
          <ChartAxisOptions
            target={target}
            unitOfMeasurement={yAxisLabel}
            isCampaignGoal={isCampaignGoal}
          />
        </LineChart>
      )
    default:
      // Default to line chart if type is not recognized
      return (
        <LineChart
          colors={resiliaPalette}
          height={chartHeight}
          margin={{
            top: height && height <= 300 ? 5 : 20,
            bottom: height && height <= 300 ? 70 : 80, // Less bottom margin for smaller charts
          }}
          xAxis={[
            {
              data: dateObjects,
              scaleType: 'time',
              tickLabelStyle: {
                angle: height && height <= 300 ? 30 : 45,
                textAnchor: 'start' as const,
                fontSize: height && height <= 300 ? 10 : 12,
              },
              tickNumber: tickCount,
              label: '', // Remove the "Date" label
              valueFormatter: (date) =>
                date.toLocaleDateString(undefined, dateFormatOptions),
            },
          ]}
          series={allSeries.map((s) => ({
            ...s,
            valueFormatter: (value: number | null) =>
              // eslint-disable-next-line no-nested-ternary
              value === null
                ? 'No data'
                : isCampaignGoal
                ? formatCurrency(value)
                : formatNumberCompact(value),
            connectNulls: true,
          }))}
          slotProps={{
            legend: legendSlotProps,
          }}
          sx={commonChartSx}
          yAxis={[
            {
              valueFormatter: formatNumberCompact,
              min: 0,
              max:
                Math.max(target ?? 0, ...allSeries.flatMap((s) => s.data)) *
                1.1,
            },
          ]}
        >
          <ChartAxisOptions
            target={target}
            unitOfMeasurement={yAxisLabel}
            isCampaignGoal={isCampaignGoal}
          />
        </LineChart>
      )
  }
}
